import { BrowserRouter as Router, Route, Routes, Link, useLocation } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import AboutUs from './AboutUs';
import PrivacyPolicy from './PrivacyPolicy';
import './App.css';

function App() {
    const [mathmlInput, setMathmlInput] = useState('');
    const [latexOutput, setLatexOutput] = useState('');
    const outputDiv = useRef(null);
    const previewDiv = useRef(null);
    const location = useLocation();

    useEffect(() => {
        if (location.pathname === '/') {
            document.title = 'Convert MathML to LaTeX Online | MathConvert';
            document.querySelector('meta[name="description"]').setAttribute('content', 'Convert MathML to LaTeX online with MathConvert. Free, fast, and accurate converter for mathematical equations. Perfect for students, educators, and researchers.');
            document.querySelector('meta[name="keywords"]').setAttribute('content', 'MathML, LaTeX, converter, online, math, equations, free, science, education, tool');
        }
    }, [location]);

    const convertMathML = async () => {
        setLatexOutput('');
        if (!mathmlInput) {
            setLatexOutput('<div class="warning-message">⚠️ Please enter MathML to convert!</div>');
            return;
        }

        try {
            let latex = window.MathMLToLaTeX.MathMLToLaTeX.convert(mathmlInput);
            latex = postProcessLatex(latex);
            setLatexOutput(latex);
            if (previewDiv.current) {
                previewDiv.current.innerHTML = `\\(${latex}\\)`;
                window.MathJax.typesetPromise([previewDiv.current]).catch((err) => console.log('Typeset failed: ' + err.message));
            }
        } catch (error) {
            setLatexOutput(`<div class="warning-message">⚠️ ${error.message}</div>`);
            if (previewDiv.current) {
                previewDiv.current.innerHTML = "";
            }
        } finally {
            // Cleanup or final actions here
        }
    };

    function postProcessLatex(latex) {
        const functions = ['sin', 'cos', 'tan', 'log', 'ln', 'exp', 'sqrt'];
        functions.forEach(fn => {
            latex = latex.replace(new RegExp(`\\b${fn}\\b`, 'g'), `\\${fn}`);
        });
        latex = latex.replace(/\\left\(\\right\.?/g, '');
        latex = latex.replace(/\\left\.?\\right\)/g, '');
        latex = latex.replace(/\\int(\^|_)/g, '\\int $1');
        latex = latex.replace(/\\sum_\{([^}]+)\}\^\{([^}]+)\}/g, '\\sum_{$1}^{$2}');
        latex = latex.replace(/\\frac\{([^{}]+)\}\{([^{}]+)\}/g, (match, num, den) => {
            return `\\frac{${num.trim()}}{${den.trim()}}`;
        });
        return latex;
    }

    const copyToClipboard = () => {
        if (outputDiv.current) {
            navigator.clipboard.writeText(outputDiv.current.textContent)
                .then(() => alert('Copied to clipboard!'))
                .catch(() => alert('Copy failed!', true));
        }
    };

    useEffect(() => {
        const scriptPolyfill = document.createElement('script');
        scriptPolyfill.src = 'https://polyfill.io/v3/polyfill.min.js?features=es6';
        scriptPolyfill.onerror = () => console.error('Failed to load polyfill.io');
        document.head.appendChild(scriptPolyfill);

        const scriptMathJax = document.createElement('script');
        scriptMathJax.src = 'https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-mml-chtml.js';
        scriptMathJax.async = true;
        scriptMathJax.onerror = () => console.error('Failed to load MathJax');
        document.head.appendChild(scriptMathJax);

        const scriptMathMLToLatex = document.createElement('script');
        scriptMathMLToLatex.src = 'https://cdn.jsdelivr.net/npm/mathml-to-latex@1.4.3/dist/bundle.min.js';
        scriptMathMLToLatex.onerror = () => console.error('Failed to load mathml-to-latex');
        document.head.appendChild(scriptMathMLToLatex);

        return () => {
            document.head.removeChild(scriptPolyfill);
            document.head.removeChild(scriptMathJax);
            document.head.removeChild(scriptMathMLToLatex);
        };
    }, []);

    return (
        <Router>
            <div className="main-wrapper">
                <header className="header">
                    <nav className="nav-container">
                        <Link to="/" className="logo">MathConvert</Link>
                        <ul className="nav-menu">
                            <li><Link to="/" className="nav-link">Home</Link></li>
                            <li><Link to="/about" className="nav-link">About Us</Link></li>
                            <li><Link to="/privacy-policy" className="nav-link">Privacy Policy</Link></li>
                            
                        </ul>
                        <button className="mobile-menu-btn" onClick={() => {
                            const menu = document.querySelector('.nav-menu');
                            menu.classList.toggle('active');
                        }}>☰</button>
                    </nav>
                </header>

                <Routes>
                    <Route path="/about" element={<AboutUs />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/" element={
                        <main className="main-content">
                            <div className="converter-container">
                                <div className="converter-section">
                                    <div className="section-header">
                                        <h2 className="section-title">MathML Input</h2>
                                        <button className="btn btn-primary" onClick={convertMathML}>
                                            <i className="fas fa-exchange-alt"></i>
                                            Convert
                                        </button>
                                    </div>
                                    <textarea
                                        id="mathml-input"
                                        placeholder="Paste MathML here..."
                                        value={mathmlInput}
                                        onChange={(e) => setMathmlInput(e.target.value)}
                                    >{`
                                        <math xmlns="http://www.w3.org/1998/Math/MathML" display="block">
                                            <mo>∫</mo>
                                            <msup><mi>sin</mi><mn>2</mn></msup>
                                            <mi>x</mi>
                                            <mi>dx</mi>
                                            <mo>=</mo>
                                            <mfrac>
                                                <mi>x</mi>
                                                <mn>2</mn>
                                            </mfrac>
                                        </math>
                                    `}</textarea>
                                </div>

                                <div className="converter-section">
                                    <div className="section-header">
                                        <h2 className="section-title">LaTeX Output</h2>
                                        <button className="btn btn-secondary" onClick={copyToClipboard}>
                                            <i className="far fa-copy"></i>
                                            Copy
                                        </button>
                                    </div>
                                    <div className="output-container fade-in" ref={outputDiv}>
                                        <pre className="latex-output" id="latex-output" dangerouslySetInnerHTML={{ __html: latexOutput }} />
                                        <div className="preview-container" id="math-preview" ref={previewDiv}></div>
                                    </div>
                                </div>
                            </div>
                        </main>
                    } />
                </Routes>

                <footer className="footer">
                    <p>© 2024 MathConvert. All rights reserved.</p>
                </footer>
            </div>
        </Router>
    );
}

export default App;
